import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import "./Dashboard.css";
import {CircularProgress} from '@mui/material'
import RefreshIcon from "@mui/icons-material/Refresh";
import AddIcon from "@mui/icons-material/Add";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import FolderOpenOutlinedIcon from "@mui/icons-material/FolderOpenOutlined";
import AccountBalanceWalletOutlinedIcon from "@mui/icons-material/AccountBalanceWalletOutlined";
import PaymentsOutlinedIcon from "@mui/icons-material/PaymentsOutlined";
import HourglassBottomOutlinedIcon from "@mui/icons-material/HourglassBottomOutlined";
import CalendarMonthOutlinedIcon from "@mui/icons-material/CalendarMonthOutlined";
import ReceiptLongOutlinedIcon from "@mui/icons-material/ReceiptLongOutlined";

import { api } from "../services/api";

const money = (n) => Number(n || 0).toLocaleString();

export default function Dashboard() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [projects, setProjects] = useState([]);
  const [allTx, setAllTx] = useState([]);

  const load = async () => {
    try {
      setLoading(true);
      setErr("");

      const projRes = await api.get("/projects");
      const projItems = projRes?.data?.projects || [];
      setProjects(projItems);

      const txPromises = projItems.map((p) =>
        api
          .get(`/projects/${p._id}/transactions`, { params: { page: 1, limit: 5000 } })
          .then((r) => ({
            projectId: p._id,
            projectName: p.name,
            items: r?.data?.items || [],
          }))
          .catch(() => ({ projectId: p._id, projectName: p.name, items: [] }))
      );

      const txByProject = await Promise.all(txPromises);

      const flat = txByProject.flatMap((x) =>
        (x.items || []).map((t) => ({
          ...t,
          _projectId: x.projectId,
          _projectName: x.projectName,
        }))
      );

      setAllTx(flat);
    } catch (e) {
      setErr(e?.response?.data?.message || e?.message || "Failed to load dashboard");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line
  }, []);

  const stats = useMemo(() => {
    const totalAmount = allTx.reduce((s, t) => s + Number(t.amount || 0), 0);
    const totalPaid = allTx.reduce((s, t) => s + Number(t.paidAmount || 0), 0);
    const totalRemaining = Math.max(totalAmount - totalPaid, 0);

    const startOfMonth = dayjs().startOf("month");
    const endOfMonth = dayjs().endOf("month");
    const monthAmount = allTx
      .filter((t) => {
        const d = dayjs(t.date);
        return d.isAfter(startOfMonth.subtract(1, "day")) && d.isBefore(endOfMonth.add(1, "day"));
      })
      .reduce((s, t) => s + Number(t.amount || 0), 0);

    const today = dayjs().format("YYYY-MM-DD");
    const todayAmount = allTx
      .filter((t) => String(t.date).slice(0, 10) === today)
      .reduce((s, t) => s + Number(t.amount || 0), 0);

    const recent = [...allTx].sort((a, b) => new Date(b.date) - new Date(a.date)).slice(0, 7);

    const perProject = projects
      .map((p) => {
        const pTx = allTx.filter((t) => t._projectId === p._id);
        const amount = pTx.reduce((s, t) => s + Number(t.amount || 0), 0);
        const paid = pTx.reduce((s, t) => s + Number(t.paidAmount || 0), 0);
        const remaining = Math.max(amount - paid, 0);
        const paidPct = amount > 0 ? Math.round((paid / amount) * 100) : 0;
        return { id: p._id, name: p.name, amount, paid, remaining, paidPct, count: pTx.length };
      })
      .sort((a, b) => b.amount - a.amount);

    const paidPctAll = totalAmount > 0 ? Math.round((totalPaid / totalAmount) * 100) : 0;

    return { totalAmount, totalPaid, totalRemaining, monthAmount, todayAmount, recent, perProject, paidPctAll };
  }, [allTx, projects]);

  return (
    <div className="db">
      <div className="db__wrap">
        {/* HERO */}
        <div className="db__hero">
          <div className="db__heroTop">
            <div style={{ minWidth: 0 }}>
              <div className="db__title">HB Expense Dashboard</div>
              <div className="db__subtitle">Track expenses & payments, project wise — clean and simple.</div>

              <div className="db__chips">
                <div className="db__chip">
                  <FolderOpenOutlinedIcon style={{ fontSize: 18 }} />
                  Projects: {projects.length}
                </div>
                <div className="db__chip">Paid: {stats.paidPctAll}%</div>
                <div className="db__chip">Today: {money(stats.todayAmount)}</div>
              </div>
            </div>

            <div className="db__actions">
              <button className="db__iconBtn" onClick={load} title="Refresh">
                <RefreshIcon />
              </button>

              <button className="db__primaryBtn" onClick={() => navigate("/projects")}>
                <AddIcon />
                Manage Projects
              </button>
            </div>
          </div>

          <div className="db__kpis">
            <Kpi icon={<AccountBalanceWalletOutlinedIcon />} label="Total Expense" value={money(stats.totalAmount)} hint="All projects combined" />
            <Kpi icon={<PaymentsOutlinedIcon />} label="Total Paid" value={money(stats.totalPaid)} hint="Payments completed" />
            <Kpi icon={<HourglassBottomOutlinedIcon />} label="Remaining" value={money(stats.totalRemaining)} hint="Pending / unpaid" />
            <Kpi icon={<CalendarMonthOutlinedIcon />} label="This Month" value={money(stats.monthAmount)} hint={dayjs().format("MMMM YYYY")} />
          </div>
        </div>

        <div className="db__sectionGap" />

        {/* STATES */}
        {loading && (
          <div className="panel">
            <div className="panel__head">
              <div>
                <div className="panel__title">Loading…</div>
                <div className="panel__desc">Fetching projects and transactions.</div>
              </div>
            </div>
            <div className="panel__body">
              <div style={{ display: "grid", placeItems: "center", padding: 18 }}>
                <CircularProgress />
              </div>
            </div>
          </div>
        )}

        {!loading && err && (
          <div className="panel" style={{ borderColor: "rgba(239,68,68,0.4)" }}>
            <div className="panel__head">
              <div>
                <div className="panel__title" style={{ color: "#fecaca" }}>Error</div>
                <div className="panel__desc" style={{ color: "rgba(255,255,255,0.75)" }}>{err}</div>
              </div>
            </div>
          </div>
        )}

        {!loading && !err && (
          <>
            <div className="db__grid">
              {/* PROJECTS */}
              <div className="panel">
                <div className="panel__head">
                  <div style={{ minWidth: 0 }}>
                    <div className="panel__title">Projects</div>
                    <div className="panel__desc">Select a project card to open transactions.</div>
                  </div>

                  <div className="panel__headRight">
                    <button className="panel__ghostBtn" onClick={() => navigate("/projects")}>View All</button>
                  </div>
                </div>

                <div className="panel__body">
                  {projects.length === 0 ? (
                    <div className="empty">
                      <div style={{ fontWeight: 950 }}>No projects yet</div>
                      <p>Create your first project (example: HB Height) and start tracking expenses.</p>
                      <button className="db__primaryBtn" onClick={() => navigate("/projects")}>
                        <AddIcon />
                        Create Project
                      </button>
                    </div>
                  ) : (
                    <div className="cardsGrid">
                      {stats.perProject.slice(0, 6).map((p) => (
                        <div
                          className="projectCard"
                          key={p.id}
                          onClick={() => navigate(`/projects/${p.id}/transactions`)}
                        >
                          <div className="projectCard__top">
                            <div className="badge">{String(p.name || "P").slice(0, 1).toUpperCase()}</div>
                            <div style={{ minWidth: 0 }}>
                              <div className="projectCard__name">{p.name}</div>
                              <div className="projectCard__meta">{p.count} transactions</div>
                            </div>

                            <div className="db__iconBtn" style={{ width: 38, height: 38 }} title="Open">
                              <ArrowForwardIcon style={{ fontSize: 18 }} />
                            </div>
                          </div>

                          <div className="metrics">
                            <Metric label="Expense" value={money(p.amount)} />
                            <Metric label="Paid" value={money(p.paid)} />
                            <Metric label="Remaining" value={money(p.remaining)} />
                          </div>

                          <div className="pillRow">
                            <div className="pill">Paid {p.paidPct}%</div>
                            <div className="pill">Open</div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* RECENT */}
              <div className="panel">
                <div className="panel__head">
                  <div style={{ minWidth: 0 }}>
                    <div className="panel__title">Recent Transactions</div>
                    <div className="panel__desc">Latest slips across all projects.</div>
                  </div>

                  <div className="panel__headRight">
                    <div className="db__chip">
                      <ReceiptLongOutlinedIcon style={{ fontSize: 18 }} />
                      Today: {money(stats.todayAmount)}
                    </div>
                  </div>
                </div>

                <div className="panel__body">
                  {stats.recent.length === 0 ? (
                    <div className="empty">
                      <div style={{ fontWeight: 950 }}>No transactions</div>
                      <p>Add expenses in any project to see history here.</p>
                      <button className="panel__ghostBtn" onClick={() => navigate("/projects")}>Open Projects</button>
                    </div>
                  ) : (
                    <div className="slips">
                      {stats.recent.map((t) => (
                        <div
                          className="slip"
                          key={t._id}
                          onClick={() => navigate(`/projects/${t._projectId}/transactions`)}
                        >
                          <div className="slip__top">
                            <div className="badge" style={{ background: "linear-gradient(135deg, rgba(16,185,129,0.8), rgba(99,102,241,0.55))" }}>
                              <ReceiptLongOutlinedIcon style={{ fontSize: 18 }} />
                            </div>

                            <div style={{ minWidth: 0 }}>
                              <div className="slip__title">{t.title}</div>
                              <div className="slip__sub">
                                {t._projectName} • {dayjs(t.date).format("DD MMM, YYYY")}
                              </div>
                            </div>

                            <div style={{ textAlign: "right" }}>
                              <div className="slip__amount">{money(t.amount)}</div>
                              <div className="slip__sub">Paid {money(t.paidAmount)}</div>
                            </div>
                          </div>

                          <div className="slip__metaRow">
                            <div className="tag">{t.paymentType === "partial" ? "Partial" : "Full"}</div>
                            <div className="tag">{t.receiverName}</div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function Kpi({ icon, label, value, hint }) {
  return (
    <div className="kpi">
      <div className="kpi__icon">{icon}</div>
      <div style={{ minWidth: 0 }}>
        <div className="kpi__label">{label}</div>
        <div className="kpi__value">{value}</div>
        <div className="kpi__hint">{hint}</div>
      </div>
    </div>
  );
}

function Metric({ label, value }) {
  return (
    <div className="metric">
      <span>{label}</span>
      <span>{value}</span>
    </div>
  );
}
